import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Trash2, Eye, EyeOff, Mail, Phone } from 'lucide-react';
import { API_BASE } from '../../lib/api';

const API = API_BASE;
const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } });

const AdminContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();

  const fetchContacts = () => {
    axios.get(`${API}/admin/contacts`, getAuth())
      .then(res => setContacts(res.data))
      .catch(e => { if (e.response?.status === 401) navigate('/admin'); });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchContacts(); }, []);

  const markRead = async (id) => {
    await axios.put(`${API}/admin/contacts/${id}/read`, {}, getAuth());
    setContacts(cs => cs.map(c => c.id === id ? { ...c, read: true } : c));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este contacto?')) return;
    await axios.delete(`${API}/admin/contacts/${id}`, getAuth());
    fetchContacts();
    if (selected?.id === id) setSelected(null);
  };

  const handleView = (c) => {
    setSelected(c);
    if (!c.read) markRead(c.id);
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString('es-ES') : '';

  return (
    <div className="p-8" data-testid="admin-contacts-page">
      <div className="mb-8">
        <h1 className="font-poppins font-bold text-2xl text-white">Contactos</h1>
        <p className="text-white/40 text-sm font-source-sans mt-1">
          {contacts.length} mensajes · {contacts.filter(c => !c.read).length} sin leer
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* List */}
        <div className="lg:col-span-2 bg-[#1A2639] border border-white/8 overflow-hidden">
          {contacts.length === 0 ? (
            <div className="text-center py-16 text-white/30 font-source-sans text-sm">No hay mensajes aún.</div>
          ) : contacts.map(c => (
            <div
              key={c.id}
              data-testid={`contact-item-${c.id}`}
              onClick={() => handleView(c)}
              className={`flex items-start gap-3 p-4 border-b border-white/5 cursor-pointer transition-colors ${
                selected?.id === c.id ? 'bg-[#7D468C]/15' : 'hover:bg-white/3'
              }`}
            >
              <div className="w-8 h-8 bg-[#7D468C]/20 flex items-center justify-center flex-shrink-0 mt-0.5" style={{ transform: 'rotate(45deg)' }}>
                <span className="font-poppins font-bold text-[#7D468C] text-xs" style={{ transform: 'rotate(-45deg)' }}>{c.name[0]}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-poppins font-semibold text-white text-sm">{c.name}</span>
                  {!c.read && <span className="w-2 h-2 bg-[#F2785C] rounded-full flex-shrink-0" />}
                </div>
                <div className="text-white/40 text-xs font-source-sans mt-0.5 truncate">{c.message}</div>
                <div className="text-white/25 text-xs font-source-sans mt-0.5">{formatDate(c.created_at)}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Detail */}
        <div className="lg:col-span-3">
          {selected ? (
            <div className="bg-[#1A2639] border border-white/8 p-6" data-testid="contact-detail">
              <div className="flex items-start justify-between mb-5">
                <div>
                  <h2 className="font-poppins font-bold text-white text-lg">{selected.name}</h2>
                  <div className="text-white/40 text-xs font-source-sans mt-0.5">{formatDate(selected.created_at)}</div>
                </div>
                <button onClick={() => handleDelete(selected.id)} className="p-2 text-white/30 hover:text-red-400 transition-colors">
                  <Trash2 size={15} />
                </button>
              </div>
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-2 text-sm font-source-sans text-white/65">
                  <Mail size={13} className="text-[#4EBFBF]" />
                  <a href={`mailto:${selected.email}`} className="hover:text-white transition-colors">{selected.email}</a>
                </div>
                {selected.phone && (
                  <div className="flex items-center gap-2 text-sm font-source-sans text-white/65">
                    <Phone size={13} className="text-[#4EBFBF]" />
                    <a href={`tel:${selected.phone}`} className="hover:text-white transition-colors">{selected.phone}</a>
                  </div>
                )}
                {selected.service && (
                  <div className="text-xs">
                    <span className="bg-[#7D468C]/20 text-[#7D468C] px-2 py-0.5 font-poppins font-semibold">{selected.service}</span>
                  </div>
                )}
              </div>
              <div className="bg-[#0F1C2E] border border-white/5 p-4">
                <p className="text-white/70 text-sm font-source-sans leading-relaxed whitespace-pre-line">{selected.message}</p>
              </div>
              <div className="mt-4">
                <a
                  href={`mailto:${selected.email}?subject=Re: Tu mensaje en Mikel Media`}
                  className="inline-flex items-center gap-2 bg-[#7D468C] text-white px-5 py-2.5 font-poppins font-semibold text-sm hover:bg-[#9D62AD] transition-colors"
                >
                  <Mail size={14} /> Responder
                </a>
              </div>
            </div>
          ) : (
            <div className="bg-[#1A2639] border border-white/8 p-6 flex items-center justify-center min-h-48">
              <p className="text-white/25 font-source-sans text-sm">Selecciona un mensaje para ver los detalles</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminContacts;
